import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/authStore'
import dashboardRoutes from '@/router/dashboardRoutes'
import authRoutes from '@/router/common/authRoutes'
import usersRoutes from '@/router/usersRoutes'
import profileRoutes from '@/router/common/profileRoutes'
import folderSetupRoutes from '@/router/repair-module/folderSetupRoutes'
import folderLifecycleRoutes from '@/router/repair-module/folderLifecycleRoutes'
import rentalFileRoutes from '@/router/rental-files/rentalFileRoutes'
import rentalProductRoutes from '@/router/rental-products/rentalProductRoutes'
import recyclingRoutes from '@/router/recycling/recyclingRoutes'
import environmentalPerformanceRoutes from '@/router/environmental-performance/environmentalPerformanceRoutes'
import createLegacyRoutes from '@/router/legacyRoutes'
import { checkPermission } from '@/utils/permissionUtils'
import i18n from '@/plugins/i18n/config.js'
import { displayToast } from '@/utils/toastUtils'

const { t } = i18n.global

const routes = [
  ...authRoutes,
  ...dashboardRoutes,
  ...usersRoutes,
  ...profileRoutes,
  ...folderSetupRoutes,
  ...folderLifecycleRoutes,
  ...rentalFileRoutes,
  ...rentalProductRoutes,
  ...recyclingRoutes,
  ...environmentalPerformanceRoutes
]

const checkRoutePermissions = (route) => {
  if (!route.meta?.permissions) {
    return true
  }

  return checkPermission(route.meta.permissions)
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

createLegacyRoutes(router).forEach((route) => router.addRoute(route))

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const isLoggedIn = authStore.auth.status.loggedIn
  const requiresAuth = to.meta?.requiresAuth

  if (to.meta?.publicCapability) {
    return next()
  }

  if (!isLoggedIn) {
    if (requiresAuth) {
      return next('/')
    }
    return next()
  }

  if (isLoggedIn) {
    if (requiresAuth && from.meta?.publicCapability) {
      await authStore.me()
      if (!authStore.auth.status.loggedIn) return next('/')
    }
    if (!requiresAuth && to.path === '/') {
      return next('/dashboard')
    }

    if (requiresAuth && !checkRoutePermissions(to)) {
      await authStore.me()

      if (!checkRoutePermissions(to)) {
        displayToast(t('COMMON.NOT_AUTHORIZED_ACTION_MESSAGE'), 'error')
        return next('/dashboard')
      }
    }
  }

  next()
})

router.onError((error) => {
  if (error?.message?.includes('Failed to fetch dynamically imported module')) {
    const alreadyReloaded = sessionStorage.getItem('chunk-reload')

    if (!alreadyReloaded) {
      sessionStorage.setItem('chunk-reload', 'true')
      window.location.reload()
    }
  }
})

export default router
